import { z } from "zod";

/**
 * Supported ISO-4217 currency codes. Start with the in-use set; extendable.
 */
export const CURRENCY_CODES = ["USD", "CAD", "AUD", "EUR", "GBP"] as const;

/** Zod enum of supported currency codes. */
export const CurrencyCodeSchema = z.enum(CURRENCY_CODES);
/** A supported ISO-4217 currency code. */
export type CurrencyCode = z.infer<typeof CurrencyCodeSchema>;

/** Default base currency used when an organization has none set. */
export const DEFAULT_CURRENCY: CurrencyCode = "USD";

/** Sources an exchange-rate snapshot can originate from. */
export const EXCHANGE_RATE_SOURCES = ["manual"] as const;

/** Zod enum of supported exchange-rate snapshot sources. */
export const ExchangeRateSourceSchema = z.enum(EXCHANGE_RATE_SOURCES);
/** Source an exchange-rate snapshot originated from. */
export type ExchangeRateSource = z.infer<typeof ExchangeRateSourceSchema>;

/**
 * Full pairwise exchange-rate table: `rates[from][to]` is the rate to convert
 * one unit of `from` into `to`. Stored explicitly (rather than derived from a
 * single base) so an organization's uploaded rates are applied exactly as
 * entered, including asymmetric and non-triangular pairs. May be partial — only
 * the pairs an organization supplies are present.
 */
export const PairwiseRatesSchema = z.record(
  z.string(),
  z.record(z.string(), z.string()),
);
/** Full pairwise rate table keyed `rates[from][to]`. */
export type PairwiseRates = z.infer<typeof PairwiseRatesSchema>;

/**
 * A point-in-time snapshot of an organization's full pairwise rate table.
 * Conversions use the explicit pairs in `rates`; the org's base currency is the
 * single source of truth for which currency a quote's amounts are denominated in.
 */
export const ExchangeRateSnapshotSchema = z.object({
  id: z.string(),
  rates: PairwiseRatesSchema,
  source: ExchangeRateSourceSchema,
  createdAt: z.coerce.date(),
});
/** A point-in-time exchange-rate snapshot. */
export type ExchangeRateSnapshot = z.infer<typeof ExchangeRateSnapshotSchema>;

/**
 * Returns the number of ISO-4217 minor-unit decimal places for a currency,
 * read from the platform's Intl data (e.g. USD → 2, JPY → 0, BHD → 3).
 * @param code - ISO-4217 currency code
 * @returns minor-unit decimal places
 */
export const getMinorUnits = (code: CurrencyCode): number =>
  new Intl.NumberFormat("en", {
    style: "currency",
    currency: code,
  }).resolvedOptions().maximumFractionDigits ?? 2;
